/*
 * Product events for sign-in through the SkySlope Forms widget (AP-4201). Forms sends the same
 * event names. Forms and DigiSign share a Heap project in staging and prod, and Heap has no
 * `product` super-property, so every event carries `product: 'sender-ui'` itself.
 *
 * Each event goes to Heap and to PostHog. PostHog is paused for DigiSign (POSTHOG_DISABLED), so it
 * is never initialized and its capture does nothing until it is turned back on.
 *
 * Never put tokens, emails or user ids in these properties.
 */
// eslint-disable-next-line import/no-named-as-default -- posthog-js re-exports its own default as a named `posthog` too
import posthog from 'posthog-js';

/** Why a widget sign-in failed. */
export type WidgetSignInFailure = 'no_token' | 'token_rejected' | 'error';

export type WidgetAnalyticsEvent =
  | {
      name: 'widget_sign_in_completed';
      properties: { success: boolean; reason: WidgetSignInFailure | null; widget_origin: string };
    }
  // A token installed after the ask gave up with no_token. Always a success. seconds_late is the
  // whole seconds from the give-up to the install.
  | { name: 'widget_sign_in_completed_late'; properties: { widget_origin: string; seconds_late: number } };

// Matches the PostHog `product` super-property (lib/posthog.ts).
const PRODUCT = 'sender-ui';

/** Sends the event to Heap and PostHog. Never throws: analytics must not break the app. */
export function trackWidgetEvent({ name, properties: eventProperties }: WidgetAnalyticsEvent): void {
  const properties = { ...eventProperties, product: PRODUCT };
  try {
    window.heap?.track(name, properties);
  } catch {
    // Heap blocked or broken: drop the event.
  }
  try {
    posthog.capture(name, properties);
  } catch {
    // PostHog blocked or broken: drop the event.
  }
}
